// src/hooks/dashboard/useTopSectors.ts
import { useMemo } from "react";
import { useLeafCategorySnapshots } from "@/hooks/dashboard/useLeafCategorySnapshots";
import { MetricKeys } from "@/constants/metrics";

export interface Sector {
  id: number;
  code: string;
  name: string;
  mcap: number;
}

interface BtcCategory {
  id: number;
  name: string;
}

interface UseTopSectorsOptions {
  /** Number of top sectors to return (default: 15) */
  count?: number;
  /** Exclude stablecoin sectors (default: true) */
  excludeStables?: boolean;
  /** Exclude "Other" sector (default: true) */
  excludeOther?: boolean;
  /** Include BTC category in results (default: false) */
  includeBtc?: boolean;
}

interface UseTopSectorsResult {
  /** Top sectors sorted by market cap */
  topSectors: Sector[];
  /** BTC category if includeBtc is true */
  btcCategory: BtcCategory | null;
  /** All leaf categories (unfiltered) */
  leaves: ReturnType<typeof useLeafCategorySnapshots>["leaves"];
  /** Loading state */
  isLoading: boolean;
}

/**
 * Hook to get top sectors by market cap with common filtering logic.
 * Eliminates duplicated sector filtering across view components.
 * 
 * @example
 * // Get top 15 sectors (default)
 * const { topSectors, isLoading } = useTopSectors();
 * 
 * @example
 * // Get top 25 sectors with BTC category
 * const { topSectors, btcCategory, isLoading } = useTopSectors({ 
 *   count: 25, 
 *   includeBtc: true 
 * });
 * 
 * @example
 * // Get multiple slice sizes
 * const { topSectors, isLoading } = useTopSectors({ count: 30 });
 * const top8 = topSectors.slice(0, 8);
 * const top15 = topSectors.slice(0, 15);
 * const top25 = topSectors.slice(0, 25);
 */
export function useTopSectors(options: UseTopSectorsOptions = {}): UseTopSectorsResult {
  const { 
    count = 15, 
    excludeStables = true, 
    excludeOther = true, 
    includeBtc = false 
  } = options;
  
  const { leaves, isLoading } = useLeafCategorySnapshots();

  const { topSectors, btcCategory } = useMemo(() => {
    let filtered = [...leaves];
    
    if (excludeOther) {
      filtered = filtered.filter((l) => l.name !== "Other");
    }
    
    if (excludeStables) {
      filtered = filtered.filter((l) => !/stable/i.test(l.name));
    }
    
    const sorted = filtered
      .map((l) => ({
        id: l.categoryId,
        code: l.code,
        name: l.name,
        mcap: Number(l.snap.current?.[MetricKeys.MarketCap] || 0),
      }))
      .filter((r) => r.mcap > 0)
      .sort((a, b) => b.mcap - a.mcap);

    const btc = includeBtc 
      ? leaves.find((l) => /\b(bitcoin|btc)\b/i.test(l.name))
      : null;

    return {
      topSectors: sorted.slice(0, count),
      btcCategory: btc ? { id: btc.categoryId, name: btc.name } : null,
    };
  }, [leaves, count, excludeStables, excludeOther, includeBtc]);

  return { 
    topSectors, 
    btcCategory, 
    leaves, 
    isLoading 
  };
}

export default useTopSectors;

